import { VStack } from '@terra/ui-styled-system';
import { SettingsBooleanControl, SettingsRow } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useSettingsHelpers } from '../hooks/useSettingsHelpers';
import {
  DEFAULT_HTTP_PROXY_PORT,
  DEFAULT_HTTPS_PROXY_PORT,
  MAX_PROXY_PORT,
  MIN_PROXY_PORT,
} from '../constants/proxy-port.constants';
import { ProxySettingsGroup } from './settings/proxy-settings/ProxySettingsGroup';
import { ProxySettingEditRow } from './settings/proxy-settings/ProxySettingEditRow';

const DEFAULT_MAX_HISTORY_SIZE = 1000;

export function GeneralTab() {
  const t = useTranslations('settings.proxyForm.general');
  const { settings, updateSetting } = useSettingsHelpers();
  const portDescription = t('portDescription', {
    min: MIN_PROXY_PORT,
    max: MAX_PROXY_PORT,
  });

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <ProxySettingsGroup>
        <ProxySettingEditRow
          kind="text"
          label={t('host')}
          description={t('hostDescription')}
          value={settings.host}
          onSave={(value) => updateSetting('host', value)}
          placeholder="localhost"
        />
        <ProxySettingEditRow
          kind="integer"
          label={t('httpPort')}
          description={portDescription}
          value={settings.port}
          min={MIN_PROXY_PORT}
          max={MAX_PROXY_PORT}
          onSave={(value) => updateSetting('port', value)}
          placeholder={String(DEFAULT_HTTP_PROXY_PORT)}
        />
        <SettingsRow
          label={t('https')}
          tooltip={t('httpsDescription')}
          control={
            <SettingsBooleanControl
              checked={settings.enableHttps}
              onCheckedChange={(details) =>
                updateSetting('enableHttps', details.checked)
              }
            />
          }
        />
        {settings.enableHttps ? (
          <ProxySettingEditRow
            kind="integer"
            label={t('httpsPort')}
            description={portDescription}
            value={settings.httpsPort}
            min={MIN_PROXY_PORT}
            max={MAX_PROXY_PORT}
            onSave={(value) => updateSetting('httpsPort', value)}
            placeholder={String(DEFAULT_HTTPS_PROXY_PORT)}
          />
        ) : null}
        <SettingsRow
          label={t('certificates')}
          tooltip={t('certificatesDescription')}
          control={
            <SettingsBooleanControl
              checked={settings.autoGenerateCerts}
              onCheckedChange={(details) =>
                updateSetting('autoGenerateCerts', details.checked)
              }
            />
          }
        />
        <ProxySettingEditRow
          kind="integer"
          label={t('maxHistory')}
          description={t('maxHistoryDescription')}
          value={settings.maxHistorySize}
          min={1}
          onSave={(value) => updateSetting('maxHistorySize', value)}
          placeholder={String(DEFAULT_MAX_HISTORY_SIZE)}
        />
      </ProxySettingsGroup>

      {settings.autoGenerateCerts ? null : (
        <ProxySettingsGroup title={t('certificatesGroup')}>
          <ProxySettingEditRow
            kind="text"
            label={t('certificatePath')}
            value={settings.certificatePath || ''}
            onSave={(value) => updateSetting('certificatePath', value)}
            placeholder="/path/to/certificate.pem"
          />
          <ProxySettingEditRow
            kind="text"
            label={t('keyPath')}
            value={settings.keyPath || ''}
            onSave={(value) => updateSetting('keyPath', value)}
            placeholder="/path/to/private-key.pem"
          />
          <ProxySettingEditRow
            kind="text"
            label={t('caCertPath')}
            description={t('caCertDescription')}
            value={settings.caCertPath || ''}
            onSave={(value) => updateSetting('caCertPath', value)}
            placeholder="/path/to/ca-certificate.pem"
          />
        </ProxySettingsGroup>
      )}
    </VStack>
  );
}
